import { useId } from 'react';

/**
 * BrandLoader — spinner de marque Inside Panama (design « Anneau + monogramme »).
 *
 * Le mini-logo (mât + drapeau panaméen) immobile au centre, un arc rouille qui
 * tourne autour. Lisible de 16 px à 96 px.
 *
 * Couleurs : l'encre (mât, quadrant + étoile navy) suit `currentColor` — navy
 * par défaut, crème en sombre (classes Tailwind). L'accent rouille et les
 * quadrants « papier » sont fixes → lisible sur crème (clair) comme sur navy
 * (sombre). Passer une classe `text-…` dans `className` force la couleur.
 *
 * @param {number}  [size=48]     Côté en px.
 * @param {boolean} [fullscreen]  Centre le loader dans un bloc ≥60vh (fallback de page).
 * @param {string}  [label]       Texte lu par les lecteurs d'écran.
 * @param {string}  [className]
 *
 * @example <Suspense fallback={<BrandLoader fullscreen />}>…</Suspense>
 * @example <BrandLoader size={20} />
 */
export default function BrandLoader({ size = 48, fullscreen = false, label = 'Chargement…', className = '' }) {
  const clip = `bl-${useId()}`;
  const hasColor = /(?:^|\s)(?:text-|dark:text-)/.test(className);
  const colorCls = hasColor ? '' : 'text-navy-900 dark:text-cream-50';

  const svg = (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      role="status"
      aria-label={label}
      className={`${colorCls} ${className}`.trim()}
    >
      <circle cx="50" cy="50" r="40" fill="none" stroke="currentColor" strokeWidth="4" opacity="0.13" />
      <circle
        cx="50"
        cy="50"
        r="40"
        fill="none"
        stroke="#BE5C3C"
        strokeWidth="4"
        strokeLinecap="round"
        strokeDasharray="60 251"
        className="animate-spin ip-loader-arc"
        style={{ transformBox: 'fill-box', transformOrigin: 'center' }}
      />
      <rect x="42" y="36" width="2.8" height="30" rx="1.4" fill="currentColor" />
      <circle cx="43.4" cy="35" r="3.1" fill="currentColor" />
      <clipPath id={clip}>
        <rect x="45" y="39" width="21" height="13" rx="2.4" />
      </clipPath>
      <g clipPath={`url(#${clip})`}>
        <rect x="45" y="39" width="10.5" height="6.5" fill="#FBF8F0" />
        <rect x="55.5" y="39" width="10.5" height="6.5" fill="#BE5C3C" />
        <rect x="45" y="45.5" width="10.5" height="6.5" fill="currentColor" />
        <rect x="55.5" y="45.5" width="10.5" height="6.5" fill="#FBF8F0" />
        <path transform="translate(50.2 42.2) scale(0.42)" d="M0,-5 1.18,-1.62 4.76,-1.55 1.9,0.62 2.94,4.05 0,2 -2.94,4.05 -1.9,0.62 -4.76,-1.55 -1.18,-1.62Z" fill="currentColor" />
        <path transform="translate(60.7 48.7) scale(0.42)" d="M0,-5 1.18,-1.62 4.76,-1.55 1.9,0.62 2.94,4.05 0,2 -2.94,4.05 -1.9,0.62 -4.76,-1.55 -1.18,-1.62Z" fill="#BE5C3C" />
      </g>
    </svg>
  );

  if (fullscreen) {
    return <div className="flex min-h-[60vh] w-full items-center justify-center">{svg}</div>;
  }
  return svg;
}
